/**
@format
@flow local-strict
*/
import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { eventNames } from '@ziynx/analytics';
import BannerNotification from '@ziynx/components/notifications';
import { Button } from '@ziynx/components/buttons';
import ActionTypes from '@ziynx/action-types';
import Colors from '@ziynx/theme/colors';
import Input from '@ziynx/components/inputs';
import Link from '@ziynx/components/links';
import { withAppManager, nameInputsRegex, emailRegex } from '@ziynx/components/utilities';
import { Body } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Form, FormRow } from '@ziynx/components/forms';
import Image from '@ziynx/components/images';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { ViewportType } from '@ziynx/flow-types/viewports';
import logo from '../../assets/ZIYNX_LOGO_2.png';
import logoSmall from '../../assets/ZIYNX_SYMBOL_2 2x.png';

type Props = $ReadOnly<{|

  /**
   * Navigation stack
   */
  history: Object,

|}>;

// display table so IE is not left out
const FormSectionWrapper = styled.div`
  display: table;
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '0px 16px 48px' : '0px 40px 48px')};
  ${({ isMobile }) => !isMobile && 'margin: 0 auto;'}
`;

const StyledBannerWrapper = styled.div`
  z-index: 996;
  width: 100%;
  height: 0;
`;

const StyledBodyWrapper = styled.div`
  ${({ lastChild }) => !lastChild && 'padding-bottom: 40px;'}
  ${({ center }) => center && 'text-align: center;'}
`;

const StyledButton = styled(Button)`
  margin-bottom: 24px;
`;

const ImageWrapper = styled.div`
  padding-bottom: 64px;
  padding-top: ${({ isMobile }) => (isMobile ? 32 : 48)}px;
`;

/**
 * Object to hold data for each types of inputs to
 * be used in Controller
 */
const inputsData = {
  data: [
    {
      id: '1',
      name: 'firstName',
      defaultValue: '',
      regExPattern: nameInputsRegex,
      errorMessage: 'Please enter a valid first name',
      requiredMessage: 'Please enter a first name',
    },
    {
      id: '2',
      name: 'lastName',
      defaultValue: '',
      regExPattern: nameInputsRegex,
      errorMessage: 'Please enter a valid last name',
      requiredMessage: 'Please enter a last name',
    },
    {
      id: '3',
      name: 'email',
      defaultValue: '',
      regExPattern: emailRegex,
      errorMessage: 'Please enter a valid email address',
      requiredMessage: 'Please enter an email address',
    },
    {
      id: '4',
      name: 'password',
      defaultValue: '',
      regExPattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^ˆ&*?_\-+=()\\|<>/{}[\]:;.,~˜`'"]{8,}$/im,
      errorMessage: 'Password is too weak. Use at least 8 characters and 1 number',
      requiredMessage: 'Please enter a password',
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Sign up screen for user to create new account
 * Each input will be validated on first blur event,
 * then will be re-validated on change event after if needed (onTouched mode)
 * @see https://react-hook-form.com/api
 */
const SignUpScreen = (props: ManagedProps<Props>): React$Node => {
  const { history, viewport } = props;
  const dispatch = useDispatch();
  const {
    handleSubmit, control, errors, setError, clearErrors, formState, watch,
  } = useForm({ mode: 'onTouched' });

  const registrationService = useSelector(({ auth }) => auth[ActionTypes.auth.signUp.GROUP]
  || {}, shallowEqual);

  const isMobileSmall = viewport === 'mobileSmall';
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  // state for banner error message based on hasError
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);

  const watchFields = watch(['email', 'password']);

  // Keeps track of all the input user modified
  const { dirtyFields } = formState;

  /**
   * Function fired when user hits create account button.
   */
  const onSubmit = async (data: Object): Promise<any> => {
    setIsLoading(true);
    const {
      email, password, firstName, lastName,
    } = data || {};
    const trimmedEmail = email?.trim();

    dispatch({
      type: ActionTypes.auth.signUp.START,
      username: trimmedEmail,
      password,
      attributes: {
        email: trimmedEmail,
        given_name: firstName?.trim(),
        family_name: lastName?.trim(),
      },
    });
  };

  useEffect(() => {
    if (!registrationService.error) {
      return;
    }
    setRenderBanner(false);
    setIsLoading(false);
    switch (registrationService?.error.code) {
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      setError('password', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      break;

    case 'UsernameExistsException':
      setError('email', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      setBannerErrorMessage(registrationService?.error?.message);
      setRenderBanner(true);
      break;

    case 'InvalidEmailRoleAccessPolicyException':
      setError('email', {
        type: 'manual',
        message: registrationService?.error?.message,
      });
      break;

    case 'CodeDeliveryFailureException':
    default:
      setBannerErrorMessage(registrationService?.error?.message);
      setRenderBanner(true);
      break;
    }
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames?.onboarding_create_account_error,
    });
    dispatch({ type: ActionTypes.auth.clearErrors });
  }, [registrationService?.error]);

  useEffect(() => {
    if (!registrationService?.success) return;
    dispatch({
      type: ActionTypes.auth.clear,
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames?.onboarding_create_account,
    });
    history.push('/confirm-code', watchFields);
  }, [registrationService?.success]);

  /**
   * Function to render Inputs for Controller render prop
   * of useForm hook
   */
  const renderInputs = ({
    onChange, onBlur, onFocus, name, value,
  }: Object): React$Node => {
    let label = '';
    let variant = 'text'; // default type of inputs
    switch (name) {
    case 'firstName':
      label = 'First name';
      break;
    case 'lastName':
      label = 'Last name';
      break;
    case 'password':
      label = 'Password';
      variant = 'password';
      break;
    case 'email':
      variant = 'email';
      label = 'Email';
      break;
    default:
      label = 'Email';
      break;
    }

    /**
     * Set value of input
     */
    const onInputChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
      (event?.target: HTMLInputElement);
      onChange(event?.target?.value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): any => {
      if (errors?.[name]) clearErrors(name);
      return onFocus;
    };

    return (
      <Input
        variant={variant}
        error={!!errors?.[name]}
        errorLabel={errors?.[name]?.message}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onBlur={onBlur}
        onFocus={onInputFocus}
        lowercase={variant === 'email'}
        value={value}
        required
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field) => (
    <FormRow key={field.id}>
      <Controller
        control={control}
        render={renderInputs}
        name={field.name}
        defaultValue={field.defaultValue}
        rules={{
          required: field?.requiredMessage,
          pattern: {
            value: field?.regExPattern,
            message: field?.errorMessage,
          },
        }}
      />
    </FormRow>
  ));

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields || isLoading) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  /**
   * To render ziynx logo as image
   */
  const renderLogo = (): React$Node => {
    if (isMobile) {
      return (
        <Image src={logoSmall} width="24px" height="24px" alt="company-logo" />
      );
    }
    return (
      <Image src={logo} width="114px" height="24px" alt="company-logo" />
    );
  };

  return (
    <>
      <Helmet>
        <title>Sign Up | ZIYNX</title>
      </Helmet>
      <ContentContainer isVertical offsetTop={0}>
        {shouldRenderBanner && (
          <StyledBannerWrapper>
            <BannerNotification animated timeout={bannerTimeOut} variant="error">
              {bannerErrorMessage}
            </BannerNotification>
          </StyledBannerWrapper>
        )}
        <FormSectionWrapper
          isMobileSmall={isMobileSmall}
          isMobile={isMobile}
          hasBanner={shouldRenderBanner}
        >
          <ImageWrapper
            isMobile={isMobile}
          >
            {renderLogo()}
          </ImageWrapper>
          <Form
            formID="sign-up"
            title="Create your account"
          >
            {renderInputsControllers()}
            <StyledBodyWrapper>
              <Body medium color={Colors.GrayDark}>
                By joining, you agree to our ZIYNX
                <Link href="https://resources.teamziynx.com/legal/TermsOfService" target="_blank"> User Agreement, </Link>
                <Link href="https://resources.teamziynx.com/legal/EndUserLicenseAgreement" target="_blank"> End User License, </Link>
                and
                <Link href="https://resources.teamziynx.com/legal/PrivacyPolicy" target="_blank"> Privacy Policy.</Link>
              </Body>
            </StyledBodyWrapper>

            <StyledButton
              disabled={isDisabled() || isError()}
              isLoading={isLoading}
              width="100%"
              form="sign-up"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Create account
            </StyledButton>

            <StyledBodyWrapper center lastChild>
              <Body medium color={Colors.GrayVeryDark}>
                Already have an account?&nbsp;
                <Link variant="navigation" activeOnlyWhenExact label="Log in" to="/" />
              </Body>
            </StyledBodyWrapper>
          </Form>
        </FormSectionWrapper>
      </ContentContainer>
    </>
  );
};

type DefaultProps = { viewport: ViewportType }

const defaultProps: DefaultProps = {
  viewport: 'desktop',
};

SignUpScreen.defaultProps = defaultProps;

export default (withAppManager(SignUpScreen): React$ComponentType<
  React$Config<ManagedProps<Props>, DefaultProps>>);
